import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Panel } from '../../components/primitives/Panel';
import type { InboxCounts, ReplyRow } from '../outreachTypes';
import { outreachMessages } from '../outreachMessages';
import { InboxGroup } from './InboxGroup';
import { groupInboxRows, INBOX_GROUP_ORDER, inboxGroupCounts } from './inboxGroups';

interface InboxListProps {
  rows: ReplyRow[];
  counts: InboxCounts | null;
  /** True when a campaign filter is set: the heads then count the loaded rows. */
  campaignFiltered: boolean;
  openUuid: string | null;
  onOpen: (replyUuid: string) => void;
  hasMore: boolean;
  onMore: () => void;
}

/**
 * The replies sorted by what each person needs (design outreach-v2 m1 left column): Needs you,
 * Added your song, Done, Hidden. Groups, not filter chips.
 */
const InboxListComponent: React.FC<InboxListProps> = ({
  rows,
  counts,
  campaignFiltered,
  openUuid,
  onOpen,
  hasMore,
  onMore,
}) => {
  const intl = useIntl();
  const groups = useMemo(() => groupInboxRows(rows), [rows]);
  const groupCounts = useMemo(
    () => inboxGroupCounts(counts, groups, campaignFiltered),
    [campaignFiltered, counts, groups]
  );
  const sections = useMemo(
    () =>
      INBOX_GROUP_ORDER.map((group) => (
        <InboxGroup
          key={group}
          group={group}
          rows={groups[group]}
          count={groupCounts[group]}
          byLabel={campaignFiltered ? null : (counts?.byLabel ?? null)}
          openUuid={openUuid}
          onOpen={onOpen}
        />
      )),
    [campaignFiltered, counts, groupCounts, groups, onOpen, openUuid]
  );
  return (
    <Panel as="section">
      {sections}
      {hasMore && (
        <div className="p-2">
          <SMLButton size="sm" outline omitCap stretch onClick={onMore}>
            {intl.formatMessage(outreachMessages.showMore)}
          </SMLButton>
        </div>
      )}
    </Panel>
  );
};

export const InboxList = TypedMemo(InboxListComponent);
